import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';

import { DEFAULT_SQUIRCLE_CORNER_RADIUS, DEFAULT_SQUIRCLE_SMOOTHING, buildSquircleClipPath } from './build-squircle-clip-path';

export interface SquircleProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Corner radius in px. Defaults to Telloport xl (24). */
    cornerRadius?: number;
    /** Figma corner smoothing 0–1. Defaults to 0.6. */
    smoothing?: number;
    /** Keep apparent smoothing at larger radii (figma-squircle). Defaults to true. */
    preserveSmoothing?: boolean;
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
    if (!ref) {
        return;
    }
    if (typeof ref === 'function') {
        ref(value);
        return;
    }
    (ref as React.MutableRefObject<T | null>).current = value;
}

/**
 * Clips children to a G2 continuous-curvature squircle.
 * Use for md+ surfaces. Keep sm/full as standard Tailwind radii.
 */
export const Squircle = forwardRef<HTMLDivElement, SquircleProps>(function Squircle(
    { cornerRadius = DEFAULT_SQUIRCLE_CORNER_RADIUS, smoothing = DEFAULT_SQUIRCLE_SMOOTHING, preserveSmoothing = true, className, style, children, ...rest },
    forwardedRef
) {
    const localRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useLayoutEffect(() => {
        const el = localRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) {
                return;
            }
            // border-box size — contentRect excludes padding and clips padded tiles
            const box = entry.borderBoxSize?.[0];
            const width = box?.inlineSize ?? entry.contentRect.width;
            const height = box?.blockSize ?? entry.contentRect.height;
            setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
        });

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const clipPath = buildSquircleClipPath({
        width: size.width,
        height: size.height,
        cornerRadius,
        smoothing,
        preserveSmoothing
    });

    return (
        <div
            ref={(node) => {
                localRef.current = node;
                assignRef(forwardedRef, node);
            }}
            className={className}
            style={{ ...style, clipPath }}
            data-squircle={clipPath ? 'true' : 'pending'}
            data-squircle-managed='component'
            {...rest}>
            {children}
        </div>
    );
});
